import { Card, CardContent, Typography, Box, IconButton, Divider } from "@mui/material";
import { Close as CloseIcon, DragIndicator, Pause, DeleteSweep, CheckCircle } from "@mui/icons-material";
import { DashboardWidget, WIDGET_TYPES } from "../../types/dashboard/DashboardConfig";
import { GetDashboardDataResponse } from "../../api/dashboard";
import AnalyticWidget from "./AnalyticWidget";
import InteractionAnalyticsBarChart from "./InteractionAnalyticsBarChart";
import MediaTypeAnalyticsChart from "./MediaTypeAnalyticsChart";
import TagWidget from "./TagWidget";
import CustomAggregateWidget from "./CustomAggregateWidget";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { numberWithCommas } from "../../helpers/numberWithCommas";

interface DashboardWidgetRendererProps {
  widget: DashboardWidget;
  dashboardData: GetDashboardDataResponse | undefined;
  isLoading: boolean;
  isEditing: boolean;
  onRemove: (widgetId: string) => void;
}

export default function DashboardWidgetRenderer({
  widget,
  dashboardData,
  isLoading,
  isEditing,
  onRemove,
}: Readonly<DashboardWidgetRendererProps>) {
  const { customerId } = useParams();
  const { t } = useTranslation("dashboards");

  const today = dayjs().utc();
  const thirtyDaysOut = today.add(30, "day").utc();
  const dateParams = new URLSearchParams({
    fromRetention: today.format("YYYY-MM-DDTHH:mm:ss"),
    toRetention: thirtyDaysOut.format("YYYY-MM-DDTHH:mm:ss"),
    hasRetention: "true",
  });

  const content = (() => {
    switch (widget.type) {
      case WIDGET_TYPES.LEGAL_HOLD:
        return (
          <AnalyticWidget
            title={t("dashboards:interactionsInLegalHold")}
            icon={<Pause fontSize="large" sx={{ color: "white" }} />}
            isLoading={isLoading}
            tooltipContent={t("dashboards:interactionsInLegalHoldTooltip")}
            linkTo={`/customers/${customerId}/search?legalHold=true`}
          >
            <Typography variant="h4" color="secondary">
              {numberWithCommas(dashboardData?.countLegalHold)}
            </Typography>
          </AnalyticWidget>
        );

      case WIDGET_TYPES.PURGING:
        return (
          <AnalyticWidget
            title={t("dashboards:purging30Days")}
            icon={<DeleteSweep fontSize="large" sx={{ color: "white" }} />}
            isLoading={isLoading}
            tooltipContent={t("dashboards:purging30DaysTooltip")}
            linkTo={`/customers/${customerId}/search?${dateParams.toString()}`}
          >
            <Typography variant="h4" color="secondary">
              {numberWithCommas(dashboardData?.countInteractionsExpiring)}
            </Typography>
          </AnalyticWidget>
        );

      case WIDGET_TYPES.TOTAL_INTERACTIONS:
        return (
          <AnalyticWidget
            title="Total Interactions"
            isLoading={isLoading}
            linkTo={`/customers/${customerId}/search`}
            icon={<CheckCircle fontSize="large" sx={{ color: "white" }} />}
          >
            <Box display="flex" gap={2} alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h5" color="secondary">
                  {dashboardData?.interactionTotal?.toLocaleString()}
                </Typography>
              </Box>
              <Divider orientation="vertical" variant="middle" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  No Media
                </Typography>
                <Typography variant="h5" color="secondary">
                  {dashboardData?.interactionTotalWithoutMedia?.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </AnalyticWidget>
        );

      case WIDGET_TYPES.TAGS:
        return (
          <TagWidget
            isLoading={isLoading}
            tagData={dashboardData?.interactionsByTags ?? []}
          />
        );

      case WIDGET_TYPES.INTERACTIONS_BY_YEAR:
        return (
          <InteractionAnalyticsBarChart
            interactionsByYear={dashboardData?.interactionsByYear}
            isBarChartLoading={isLoading}
          />
        );

      case WIDGET_TYPES.MEDIA_TYPE:
        return (
          <MediaTypeAnalyticsChart
            data={dashboardData?.interactionByType ?? null}
            isLoading={isLoading}
          />
        );

      case WIDGET_TYPES.CUSTOM_AGGREGATE:
        return (
          <CustomAggregateWidget widget={widget} />
        );

      default:
        return (
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography color="text.secondary">
                Unknown widget type: {widget.type}
              </Typography>
            </CardContent>
          </Card>
        );
    }
  })();

  return (
    <Box sx={{ height: "100%", position: "relative", "& .MuiCard-root": { border: 1, borderColor: "divider" } }}>
      {isEditing && (
        <Box
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 10,
            display: "flex",
            gap: 0.5,
          }}
        >
          <IconButton
            size="small"
            className="drag-handle"
            sx={{
              cursor: "grab",
              bgcolor: "rgba(0,0,0,0.04)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
            }}
          >
            <DragIndicator fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onRemove(widget.id)}
            sx={{
              bgcolor: "rgba(0,0,0,0.04)",
              "&:hover": { bgcolor: "error.light", color: "white" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
      {content}
    </Box>
  );
}
